import type { MenuConfig } from '@blocksuite/affine-components/context-menu';
import type { UniComponent } from '@blocksuite/affine-shared/types';

import { createUniComponentFromWebComponent } from '../utils/uni-component/index.js';
import type { Property } from '../view-manager/property.js';
import type { BaseCellRenderer } from './base-cell.js';
import type { CellRenderer, DataViewCellComponent } from './manager.js';

export interface PropertyConfigMenuContext<
  Data extends NonNullable<unknown> = NonNullable<unknown>,
> {
  property: Property<unknown, unknown, Data>;
  closeMenu: () => void;
}

export interface PropertyLifecycleContext<
  Data extends NonNullable<unknown> = NonNullable<unknown>,
> {
  property: Property<unknown, unknown, Data>;
}

export interface PropertyComputedValueContext<
  Data extends NonNullable<unknown> = NonNullable<unknown>,
  RawValue = unknown,
  JsonValue = unknown,
> {
  property: Property<RawValue, JsonValue, Data>;
  rowId: string;
  rawValue: RawValue | undefined;
}

export interface Renderer<
  Data extends NonNullable<unknown> = NonNullable<unknown>,
  RawValue = unknown,
  JsonValue = unknown,
> {
  type: string;
  icon?: UniComponent;
  cellRenderer: CellRenderer<Data, RawValue, JsonValue>;
  propertyConfigMenu?: (ctx: PropertyConfigMenuContext<Data>) => MenuConfig[];
  onBeforeColumnDelete?: (ctx: PropertyLifecycleContext<Data>) => void;
  computedJsonValue?: (
    ctx: PropertyComputedValueContext<Data, RawValue, JsonValue>
  ) => JsonValue | undefined;
}

export const createFromBaseCellRenderer = <
  RawValue = unknown,
  JsonValue = unknown,
  Data extends Record<string, unknown> = Record<string, unknown>,
>(
  renderer: new () => BaseCellRenderer<RawValue, JsonValue, Data>
): DataViewCellComponent => {
  return createUniComponentFromWebComponent(renderer as never) as never;
};
